import {v4 as uuid} from 'uuid';

import type {
  ApiImageryCheckoutItem,
  ApiImageryCheckoutOptions,
  ApiImageryCheckoutPayment,
  ApiImageryCheckoutResult,
} from 'app/modules/Remote/api';

import type {CheckoutFailure} from './checkoutErrors';

/**
 * What one pay-now checkout is identified by. The pair travels together: the server builds every
 * Stripe idempotency key behind the checkout's invoice from `checkoutKey`, and keys its one confirm
 * on that key, so the same token has to come back with it for a retry to finish that charge rather
 * than be turned down. A checkout prepaid credit covers in full has no token until the server asks
 * for a card, and then gets one on its same key: no card has been charged under it.
 */
export interface CheckoutAttempt extends ApiImageryCheckoutOptions {
  /**
   * Set once an answer says a charge may exist under this key, or an answer was lost. From then on
   * no rejection retires the key, only the order committing or the card being declined.
   */
  mayHaveCharged: boolean;
  /**
   * The order, as `checkoutContents` identifies it, that the server asked a card for while this
   * attempt had none. Kept here so the ask outlives the page; a changed order hasn't been asked.
   */
  cardNeededFor?: string;
}

/** Identifies what a checkout is for, so a card the server asked for is only asked of that order. */
export const checkoutContents = (items: ApiImageryCheckoutItem[]): string => JSON.stringify(items);

/** How one checkout call landed, with rejections paired to the submitted `Item`s. */
export type CheckoutOutcome<Item> =
  | {kind: 'success'; result: ApiImageryCheckoutResult}
  /**
   * A 200 whose charge didn't go through. The endpoint orders nothing unless the money is
   * collected, so nothing was ordered. `actionError` is what a 3D Secure challenge came back with,
   * when one ran.
   */
  | {kind: 'unpaid'; payment: ApiImageryCheckoutPayment; actionError?: string}
  | {kind: 'failure'; failure: CheckoutFailure<Item>};

// What a challenge says when the payment form isn't mounted to run it, such as mid-remount.
export const CHALLENGE_UNAVAILABLE_MESSAGE = 'Your bank’s check couldn’t be opened.';

/** What a group's attempt sends with its cart. */
export function checkoutOptions(attempt: CheckoutAttempt): ApiImageryCheckoutOptions {
  const {confirmationTokenId, checkoutKey, note} = attempt;
  return {...(confirmationTokenId === undefined ? {} : {confirmationTokenId}), checkoutKey, note};
}

/**
 * A charge that didn't go through. Nothing is ordered unless the money is collected, so this
 * reports an order that did not happen and, from the cart, rows that are still in it.
 */
export interface PaymentNotice {
  tone: 'warning' | 'danger';
  title: string;
  message: string;
}

type PlacedPayment = Extract<ApiImageryCheckoutPayment, {status: 'paid' | 'processing'}>;

/** A charge that orders: collected, or a bank debit Stripe has accepted and settles in days. */
const isPlaced = (payment: ApiImageryCheckoutPayment): payment is PlacedPayment =>
  payment.status === 'paid' || payment.status === 'processing';

/**
 * How a 200 from the checkout endpoint landed. A pay-now checkout orders nothing unless its charge
 * came back paid, or processing for a bank debit — the endpoint answers an unsettled one with an
 * empty `items` — so only those results are an order. A contract-billed checkout carries no invoice
 * and is ordered on the 200.
 */
export function checkoutOutcome(
  result: ApiImageryCheckoutResult
): Extract<CheckoutOutcome<never>, {kind: 'success' | 'unpaid'}> {
  const payment = result.invoice?.payment;
  return payment && !isPlaced(payment) ? {kind: 'unpaid', payment} : {kind: 'success', result};
}

/**
 * What a committed checkout cost: a card's charge, fees and tax included, or what the contract was
 * billed.
 */
export function chargedCents(result: ApiImageryCheckoutResult): {cents: number; taxCents: number} {
  return result.invoice
    ? {cents: result.invoice.amountDueCents, taxCents: result.invoice.taxCents}
    : {cents: result.totalCents, taxCents: 0};
}

/**
 * What a checkout should say about a charge that came back anything but `paid` or `processing`,
 * once any 3D Secure challenge has run. `actionError` is the message the challenge came back with;
 * null means none ran or it cleared, which only reaches here when the resubmit behind it still
 * didn't settle.
 *
 * Each message leads with what the server or Stripe said and adds only what that can't know: which
 * of these leaves the cart standing, and what to do next. No server message mentions the cart, and
 * each pending one already says what to do next, so nothing here repeats a sentence the buyer is
 * about to read anyway.
 */
export function paymentNotice(
  payment: ApiImageryCheckoutPayment,
  actionError: string | null
): PaymentNotice | null {
  if (isPlaced(payment)) {
    return null;
  }
  switch (payment.status) {
    // Stripe's own copy, from the browser rather than the server, so it names the authentication
    // and nothing else.
    case 'requiresAction':
      return {
        tone: 'danger',
        title: 'Your card couldn’t be confirmed',
        message: `${
          actionError ?? 'Your bank didn’t confirm this payment.'
        } Nothing in your cart was ordered. Submit again to retry.`,
      };
    // A challenge the buyer failed or closed comes back declined, and Stripe's message for the
    // challenge says more than the server's.
    case 'failed':
      if (actionError) {
        return {
          tone: 'danger',
          title: 'Your card couldn’t be confirmed',
          message: `${actionError} Nothing in your cart was ordered.`,
        };
      }
      // Either Stripe's decline message for this payment method or the server's fallback, which
      // already says to try a different one.
      return {
        tone: 'danger',
        title: 'Your payment was declined',
        message: `${payment.failureMessage} Nothing in your cart was ordered.`,
      };
    // Either the server couldn't tell whether the card was charged — Stripe errored or timed out, or
    // left the payment unsettled — and the cart has already resent it on its own, or a checkout sent
    // without a card still has something to pay, which no resend can settle. Submitting again
    // finishes this same checkout, with the card for the latter, rather than charging a second time.
    // The server's message says which, and what to do.
    case 'pending':
      return {
        tone: 'warning',
        title: 'We couldn’t finish your checkout',
        message: payment.failureMessage,
      };
  }
}

/**
 * What one of the server's checkout-level messages means. `key` is what becomes of the checkout's
 * key: `spent` can't be used again, `kept` stays as it is, `inFlight` is still held by a sibling
 * request, and `mayHaveCharged` may be sitting on a charge.
 */
interface ServerCopy {
  opens: string;
  key?: 'spent' | 'kept' | 'inFlight' | 'mayHaveCharged';
  /** The order needs a card the checkout wasn't sent. */
  asksForCard?: true;
  /** The credit the page shows, in previews and the balance, is out of date. */
  outdatesCredit?: true;
  /** Written for the buyer: it says what happened and what to do next. */
  forBuyer?: true;
}

// The server's checkout-level copy that the browser acts on, matched by how each message opens.
// Anything not written for the buyer, such as a framework error, the request layer's own copy, or a
// message worded for API clients, says nothing a buyer can act on. An allowlist fails safe: a
// reworded message falls back to the generic failure rather than showing the buyer something it
// wasn't written for.
const SERVER_COPY: ServerCopy[] = [
  // Answered before anything is written, so the same key can go again with a card.
  {
    opens: "Your imagery credit doesn't cover this whole order.",
    key: 'kept',
    asksForCard: true,
    outdatesCredit: true,
    forBuyer: true,
  },
  // The credit a reopened key held was spent by another order in the meantime.
  {
    opens: 'Your imagery credit changed while you were checking out',
    key: 'spent',
    outdatesCredit: true,
    forBuyer: true,
  },
  // A checkout sent without a card had something due after all, so its invoice was deleted.
  {
    opens: 'Your imagery credit no longer covers this whole order',
    key: 'spent',
    asksForCard: true,
    outdatesCredit: true,
    forBuyer: true,
  },
  // The credit coupon didn't zero the lines it was put on, so the invoice was deleted.
  {opens: "We couldn't apply your imagery credit", key: 'spent', forBuyer: true},
  {opens: "Your organization's billing account has a balance", forBuyer: true},
  // The second is the older wording, which a server from before the rewording still sends.
  {opens: 'This payment is still being processed.', key: 'inFlight', forBuyer: true},
  {opens: 'This checkout is already being processed.', key: 'inFlight', forBuyer: true},
  {opens: 'Our payment provider had a problem', forBuyer: true},
  // A key whose one payment attempt is used up, or whose order is settled one way or the other.
  {opens: 'This checkout already has a payment attempt', key: 'spent', forBuyer: true},
  {opens: 'This checkoutKey already has a payment attempt', key: 'spent'},
  {opens: 'This checkoutKey belongs to a different order', key: 'spent'},
  // The cart changed under a key whose invoice may already have collected.
  {opens: "This checkout couldn't be resumed.", key: 'mayHaveCharged', forBuyer: true},
  {opens: "A recent payment for this organization hasn't been recorded yet", forBuyer: true},
  {opens: 'After discounts this order comes to less than', forBuyer: true},
  {opens: "Billing isn't set up for this organization yet.", forBuyer: true},
  // A `pending` payment's message for a checkout sent without a card whose invoice still has
  // something to pay. Every other `pending` is a charge the server couldn't confirm, including a $0
  // invoice that settled but wasn't committed, and a resend of the same key finishes it.
  {opens: 'Add a card to finish this checkout.', asksForCard: true},
];

const copyFor = (message: string): ServerCopy | undefined =>
  SERVER_COPY.find(({opens}) => message.startsWith(opens));

const copyOf = (failure: CheckoutFailure<unknown>): ServerCopy | undefined =>
  failure.kind === 'itemRejections' ? undefined : copyFor(failure.message);

/** The server's message for a checkout-level failure when it's written for the buyer, or null. */
export function buyerMessage(failure: CheckoutFailure<unknown>): string | null {
  return failure.kind !== 'itemRejections' && copyFor(failure.message)?.forBuyer
    ? failure.message
    : null;
}

/** The answer was lost on the way back, so it says nothing about a charge it may be sitting on. */
function isLost(failure: CheckoutFailure<unknown>): boolean {
  return failure.kind === 'unknown' && (failure.status === undefined || failure.status >= 500);
}

/** A sibling request on the same key still holds it, and finishes on its own. */
export function isInFlight(failure: CheckoutFailure<unknown>): boolean {
  return failure.kind === 'unknown' && copyFor(failure.message)?.key === 'inFlight';
}

/** A rejection that says nothing about the checkout itself. Either kind could be sitting on a charge. */
const isTransient = (failure: CheckoutFailure<unknown>) => isLost(failure) || isInFlight(failure);

/** Whether an answer says the order needs a card. */
export function asksForCard(outcome: CheckoutOutcome<unknown>): boolean {
  if (outcome.kind === 'unpaid') {
    return (
      outcome.payment.status === 'pending' &&
      copyFor(outcome.payment.failureMessage)?.asksForCard === true
    );
  }
  return outcome.kind === 'failure' && copyOf(outcome.failure)?.asksForCard === true;
}

/** The attempt to keep after an answer, marked with the order when the answer asked for a card. */
export const withCardRequest = (
  attempt: CheckoutAttempt | null,
  outcome: CheckoutOutcome<unknown>,
  contents: string
): CheckoutAttempt | null =>
  attempt && asksForCard(outcome) ? {...attempt, cardNeededFor: contents} : attempt;

/** Whether an answer leaves the credit the page shows, in previews and the balance, out of date. */
export function outdatesCredit(outcome: CheckoutOutcome<unknown>): boolean {
  return (
    outcome.kind === 'success' ||
    (outcome.kind === 'failure' && copyOf(outcome.failure)?.outdatesCredit === true)
  );
}

/**
 * A group's attempt once an answer has come back, or null when the server is done with its key.
 * A fresh key starts a second charge, so a key is given up only where nothing more can be charged
 * under it: the order committed, the card was declined, its invoice is gone, or a rejection turned
 * the cart down before any answer said a charge might exist.
 */
export function nextAttempt(
  attempt: CheckoutAttempt,
  outcome: CheckoutOutcome<unknown>
): CheckoutAttempt | null {
  switch (outcome.kind) {
    case 'success':
      return null;
    // A decline is terminal for its key: the server keys its one confirm on it, so another card
    // needs a checkout of its own. Anything else unpaid can still collect money: clearing a
    // challenge charges the card without ordering anything, and a pending charge can still settle.
    // Only a resend on this key turns that money into an order; a fresh key would charge again.
    case 'unpaid':
      return outcome.payment.status === 'failed' ? null : {...attempt, mayHaveCharged: true};
    case 'failure': {
      const key = copyOf(outcome.failure)?.key;
      if (key === 'spent') {
        return null;
      }
      if (isTransient(outcome.failure) || key === 'mayHaveCharged') {
        return {...attempt, mayHaveCharged: true};
      }
      if (key === 'kept') {
        return attempt;
      }
      // Every other rejection comes before the server charges, so it says nothing about a charge
      // an earlier answer raised — a resubmit after a cleared challenge can still be turned down.
      return attempt.mayHaveCharged ? attempt : null;
    }
  }
}

// Short enough that the buyer is still watching the spinner. Past this, submitting again is left to
// the buyer, on the same key.
export const RESEND_DELAYS_MS = [1000, 3000];
// A sibling request on the key finishes on its own, and its answer is the buyer's, so it's worth
// about a minute's wait before the buyer is told to come back later.
export const IN_FLIGHT_RESEND_DELAYS_MS = [2000, 4000, 8000, 16000, 30000];

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface SettleSteps<Item> {
  /** Sends the group again on its same key and token. */
  resend: () => Promise<CheckoutOutcome<Item>>;
  /** Runs a 3D Secure challenge. Resolves to the message to show when it didn't clear. */
  challenge: (clientSecret: string) => Promise<string | null>;
  /** `inFlight` waits on a sibling request still holding the key, which can take a minute. */
  wait: (ms: number, reason: 'settling' | 'inFlight') => Promise<void>;
}

/**
 * Carries a group's first answer as far as the browser can take it on the same key and token:
 * through one 3D Secure challenge, through resends of a charge still settling or an answer that was
 * lost, and through a sibling request on the key that hasn't finished. The server commits a charge
 * it collected when it sees it again, so leaving the resend to the buyer risks a paid card with no
 * order if they walk away.
 *
 * Clearing a challenge settles the payment but commits nothing — the order is built only where the
 * server sees the money — so every challenge is followed by a resend, whatever it came back with,
 * and the server's live read of the payment is the verdict.
 */
export async function settleCheckout<Item>(
  attempt: CheckoutAttempt,
  first: CheckoutOutcome<Item>,
  steps: SettleSteps<Item>
): Promise<{outcome: CheckoutOutcome<Item>; attempt: CheckoutAttempt | null}> {
  let outcome = first;
  let current = nextAttempt(attempt, outcome);
  let actionError: string | null = null;
  let challenged = false;
  let resends = 0;
  let inFlightWaits = 0;
  while (current) {
    if (outcome.kind === 'unpaid' && outcome.payment.status === 'requiresAction' && !challenged) {
      challenged = true;
      actionError = await steps.challenge(outcome.payment.clientSecret);
    } else if (outcome.kind === 'failure' && isInFlight(outcome.failure)) {
      if (inFlightWaits === IN_FLIGHT_RESEND_DELAYS_MS.length) {
        break;
      }
      await steps.wait(IN_FLIGHT_RESEND_DELAYS_MS[inFlightWaits], 'inFlight');
      inFlightWaits += 1;
    } else if (
      resends < RESEND_DELAYS_MS.length &&
      // A resend can't give the server a card it asked for.
      ((outcome.kind === 'unpaid' &&
        outcome.payment.status === 'pending' &&
        !asksForCard(outcome)) ||
        (outcome.kind === 'failure' && isLost(outcome.failure)))
    ) {
      await steps.wait(RESEND_DELAYS_MS[resends], 'settling');
      resends += 1;
    } else {
      break;
    }
    outcome = await steps.resend();
    current = nextAttempt(current, outcome);
  }
  if (outcome.kind === 'unpaid' && actionError) {
    outcome = {...outcome, actionError};
  }
  return {outcome, attempt: current};
}

/**
 * What a cart group sends on Submit. An attempt already waiting is finished on its own key, and one
 * that went without a card gets one there when the order now needs it. Null means the card couldn't
 * be tokenized, so nothing is sent.
 */
export async function submitAttempt(
  waiting: CheckoutAttempt | undefined,
  {
    paysByCard,
    note,
    tokenize,
  }: {
    paysByCard: boolean;
    note: string | undefined;
    /** Resolves to a confirmation token id, or null when the card needs fixing. */
    tokenize: () => Promise<string | null>;
  }
): Promise<CheckoutAttempt | null> {
  if (waiting && (waiting.confirmationTokenId !== undefined || !paysByCard)) {
    return waiting;
  }
  if (!paysByCard) {
    return {checkoutKey: uuid(), mayHaveCharged: false, note};
  }
  const confirmationTokenId = await tokenize();
  if (confirmationTokenId === null) {
    return null;
  }
  return waiting
    ? {...waiting, confirmationTokenId}
    : {confirmationTokenId, checkoutKey: uuid(), mayHaveCharged: false, note};
}

/** Collapses the per-group notices a multi-contract cart can raise into one of each kind. */
export function dedupeNotices(notices: PaymentNotice[]): PaymentNotice[] {
  const byText = new Map(notices.map((notice) => [`${notice.title}~${notice.message}`, notice]));
  return [...byText.values()];
}
